import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useResolveFormSchema } from "../../../../../_hooks/useResolveFormSchema";
import fieldsToSections from "../../../../../_utils/fieldsToSections";
import { useGetAllObjectCustomizationsQuery } from "../../../../objectCustomization/api/objectCustomizationApi";

/**
 * Builds CompanyFormModal's field-schema sections from the org's active
 * "company" object customizations, resolving any dynamic (external
 * reference) fields' data along the way.
 *
 * The Redux slice this normally reads from is only ever populated by the
 * internal login/org-selection flow (`useOrganisationSelection`) - it stays
 * at its `null` initial value for the whole session on a public page like
 * Pay, where a customer never logs into an org. That silently degraded to a
 * genuinely empty (not loading, not errored) 0-field schema, so the modal's
 * body rendered blank. `orgId` (passed by callers that know it without a
 * login session, e.g. Pay resolving it from its own decrypted `paymentId`)
 * lets this fall back to fetching directly instead of only ever trusting
 * that Redux slice.
 */
export function useCompanyFormSchema(orgId) {
  const objectCustomizations = useSelector(
    (state) => state?.objectCustomizationFeature?.objectCustomizations
  );

  const { data: fetchedObjectCustomizations } = useGetAllObjectCustomizationsQuery(
    { orgId },
    { skip: !orgId || Boolean(objectCustomizations) }
  );

  const companyCustomization = useMemo(() => {
    const customization =
      objectCustomizations?.data?.["company"] || fetchedObjectCustomizations?.data?.["company"];
    return Array.isArray(customization) ? customization : [];
  }, [objectCustomizations, fetchedObjectCustomizations]);

  const companyFormSchema = useMemo(() => {
    const fields = companyCustomization.filter(
      (oc) => oc.status === "active" && oc.showDuringCreation
    );
    return fieldsToSections(fields);
  }, [companyCustomization]);

  const resolvedSchema = useResolveFormSchema(companyFormSchema);

  return useMemo(() => resolvedSchema, [resolvedSchema]);
}
